const API_BASE = "/api";

export interface FriendInfo {
  name: string;
  lastSeen: number | null;
  online: boolean;
  /** This friend has an open "join my race" invite waiting for you. */
  inviteFromThem: boolean;
  /** Unread DM count from this friend (from the heartbeat). */
  unread?: number;
}

/** Compact "my clan" summary carried by the heartbeat. */
export interface ClanSummary {
  id: number;
  name: string;
  isLeader: boolean;
  /** Pending join requests for the clan (leader-only; drives the menu badge). */
  requests?: number;
}

interface FriendsPayload {
  friends: FriendInfo[];
  incoming: string[];
  outgoing: string[];
  global?: number;
  clanUnread?: number;
  dmUnread?: number;
  clan?: ClanSummary | null;
}

/**
 * Thin API client + cache for the friends system. The periodic `refresh()` call
 * doubles as the presence heartbeat (the GET endpoint bumps the caller's
 * `last_seen`), so friends see each other as online while the game is open.
 *
 * Mutations (request/accept/…) return the fresh snapshot, which is applied
 * directly to the cache so the UI updates with no second round-trip. A monotonic
 * ticket guard ensures an out-of-order *stale* response (e.g. a slow background
 * poll resolving after a newer mutation) can never overwrite newer state.
 */
export class Friends {
  private friends: FriendInfo[] = [];
  private incoming: string[] = [];
  private outgoing: string[] = [];
  private globalUnreadN = 0;
  private clanUnreadN = 0;
  private dmUnreadN = 0;
  private clan: ClanSummary | null = null;
  private seq = 0;
  private applied = 0;

  list(): FriendInfo[] {
    return this.friends.slice();
  }
  incomingRequests(): string[] {
    return this.incoming.slice();
  }
  outgoingRequests(): string[] {
    return this.outgoing.slice();
  }

  /** Unread counts + clan summary carried by the heartbeat (drive menu badges). */
  globalUnread(): number {
    return this.globalUnreadN;
  }
  clanUnread(): number {
    return this.clanUnreadN;
  }
  dmUnread(): number {
    return this.dmUnreadN;
  }
  myClan(): ClanSummary | null {
    return this.clan;
  }
  /** Pending clan join requests awaiting the leader (0 if not a leader). */
  clanRequestCount(): number {
    return this.clan?.requests ?? 0;
  }

  /** Incoming friend requests + open race invites — drives the menu badge. */
  pendingCount(): number {
    return this.incoming.length + this.friends.filter((f) => f.inviteFromThem).length;
  }

  clear(): void {
    this.friends = [];
    this.incoming = [];
    this.outgoing = [];
    this.globalUnreadN = 0;
    this.clanUnreadN = 0;
    this.dmUnreadN = 0;
    this.clan = null;
  }

  /** Apply a payload to the cache unless an already-applied response is newer. */
  private applyPayload(ticket: number, data: Partial<FriendsPayload>): void {
    if (ticket < this.applied || !Array.isArray(data.friends)) return;
    this.applied = ticket;
    this.friends = data.friends;
    this.incoming = Array.isArray(data.incoming) ? data.incoming : [];
    this.outgoing = Array.isArray(data.outgoing) ? data.outgoing : [];
    // Chat/clan badges (present on the heartbeat GET; absent on mutation responses
    // — leave the prior values untouched then so a request/accept doesn't zero them).
    if (typeof data.global === "number") this.globalUnreadN = data.global;
    if (typeof data.clanUnread === "number") this.clanUnreadN = data.clanUnread;
    if (typeof data.dmUnread === "number") this.dmUnreadN = data.dmUnread;
    if (data.clan !== undefined) this.clan = data.clan ?? null;
  }

  /** Heartbeat + pull friends/requests/presence. Safe to call on a timer. */
  async refresh(name: string, token: string): Promise<void> {
    if (!name || !token) return;
    const ticket = ++this.seq;
    try {
      const res = await fetch(`${API_BASE}/friends?name=${encodeURIComponent(name)}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      this.applyPayload(ticket, (await res.json()) as Partial<FriendsPayload>);
    } catch {
      // leave cache in place on network errors
    }
  }

  request(name: string, token: string, target: string): Promise<{ ok: boolean; error?: string }> {
    return this.post("/friends/request", token, { name, target });
  }
  accept(name: string, token: string, requester: string): Promise<{ ok: boolean; error?: string }> {
    return this.post("/friends/accept", token, { name, requester });
  }
  decline(name: string, token: string, requester: string): Promise<{ ok: boolean; error?: string }> {
    return this.post("/friends/decline", token, { name, requester });
  }
  cancelRequest(name: string, token: string, target: string): Promise<{ ok: boolean; error?: string }> {
    return this.post("/friends/cancel", token, { name, target });
  }
  remove(name: string, token: string, friend: string): Promise<{ ok: boolean; error?: string }> {
    return this.post("/friends/remove", token, { name, friend });
  }

  private async post(
    path: string,
    token: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; error?: string }> {
    const ticket = ++this.seq;
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<FriendsPayload> & {
        error?: string;
      };
      if (res.ok) {
        // The mutation returns the fresh snapshot — apply it so the UI reflects
        // the change instantly without waiting for the next poll.
        this.applyPayload(ticket, data);
        return { ok: true };
      }
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }
}
